// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\analytics\DelegatesSummaryPage.js

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Table, Typography, Tag, Row, Col, Select, DatePicker, 
  Button, Form, Badge, Card, Space, message, Tooltip 
} from 'antd'; 
import { 
  DownloadOutlined, InfoCircleOutlined, UserOutlined, RocketOutlined
} from '@ant-design/icons';
import Papa from 'papaparse';
import { getDelegatesSummary, getDelegatesList } from '../../redux/slices/delegates/delegatesThunk';
import { getRegions } from '../../redux/slices/regions/regionsThunk';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DelegatesSummaryPage = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const { data: summaryData, loading: summaryLoading } = useSelector((state) => state.delegates.delegatesSummary);
  const { data: delegatesListResponse } = useSelector((state) => state.delegates.delegatesList);
  const { regions: regionsListResponse } = useSelector((state) => state.regions);
  
  const dateRange = Form.useWatch('dateRange', form);
  const startDate = dateRange && dateRange[0] ? dateRange[0] : dayjs('2026-03-01');
  const endDate = dateRange && dateRange[1] ? dateRange[1] : dayjs('2026-03-31');

  useEffect(() => {
    console.log(">>> ÉTAT summaryData CHANGE :", summaryData);
    if (summaryData) {
      console.log(">>> Nombre d'éléments :", summaryData.length);
      const rania = summaryData.find(d => d.lastname.toUpperCase().includes('BENDAI'));
      if (rania) {
        console.log(">>> FOCUS RANIA :", {
            nom: rania.lastname,
            totalVisites: rania.reportCount,
            clesVisitMap: Object.keys(rania.visitMap),
            data31Mars: rania.visitMap['2026-03-31']
        });
      }
    }
  }, [summaryData]);

  useEffect(() => {
    dispatch(getDelegatesSummary({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD')
    }));
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
    dispatch(getRegions({ page: 1, itemsPerPage: 1000 }));
  }, [dispatch]);

  const onFinish = (values) => {
    // On prépare un objet propre avec des chaînes de caractères
    const filters = {
      delegateIds: values.delegateIds,
      regionIds: values.regionIds,
      // Formatage explicite ici
      startDate: values.dateRange ? values.dateRange[0].format('YYYY-MM-DD') : undefined,
      endDate: values.dateRange ? values.dateRange[1].format('YYYY-MM-DD') : undefined
    };
    
    console.log("Filtres formatés envoyés :", filters);
    dispatch(getDelegatesSummary(filters));
  };

  const getDayData = (delegate, currentDate) => {
    const dateKey = currentDate.format('YYYY-MM-DD');
    const visitData = delegate.visitMap ? delegate.visitMap[dateKey] : null;
    const holiday = delegate.holidays?.find(h => h.date === dateKey);
    const isLeave = delegate.leaves?.some(l => dateKey >= l.start && dateKey <= l.end);

    return {
        dateStr: currentDate.format('DD/MM/YYYY'),
        dayName: currentDate.format('dddd'),
        regionsText: visitData ? visitData.regionsText : '-',
        addressesText: visitData ? visitData.addressesText : '-', 
        totalVisits: visitData ? visitData.count : 0,
        eventName: visitData ? visitData.eventName : '-',
        holiday: holiday ? holiday.name : '-',
        isLeave: !!isLeave,
        // ⚡ NOUVELLES COLONNES
        optimalKm: visitData?.optimalKm !== undefined ? visitData.optimalKm : null,
        realKm: visitData?.realKm !== undefined ? visitData.realKm : null,
        optimalPathText: visitData?.optimalPathText || '-',
    };
};

  const handleExportCSV = () => {
    if (!summaryData || summaryData.length === 0) return message.warning("Pas de données");
    
    const exportRows = [];
    
    summaryData.forEach(delegate => {
      let current = dayjs(startDate);
      
      while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
        const dayInfo = getDayData(delegate, current);
        
        exportRows.push({
          'Délégué': `${delegate.firstname} ${delegate.lastname}`.toUpperCase(),
          'Date': dayInfo.dateStr,
          'Jour': dayInfo.dayName,
          'Régions visitées': dayInfo.regionsText,
          'Adresses visitées': dayInfo.addressesText,
          'Nombre de visites': dayInfo.totalVisits,
          'Km Estimé (Optimal)': dayInfo.optimalKm !== null ? `${dayInfo.optimalKm} km` : '-',
          'Km Réel (Visites)': dayInfo.realKm !== null ? `${dayInfo.realKm} km` : '-',
          'Ordre Optimal Conseillé': dayInfo.optimalPathText,
          'Congés': dayInfo.isLeave ? 'CONGÉ' : '-',
          'Jours Fériés': dayInfo.holiday,
          'Événement': dayInfo.eventName,
      });
        
        current = current.add(1, 'day');
      }
      
      // Ligne vide pour séparer les délégués dans le fichier Excel
      exportRows.push({}); 
    });

    const csv = Papa.unparse(exportRows, { delimiter: ';' });
    
    // Ajout du BOM UTF-8 (\uFEFF) pour que Excel affiche bien les accents tunisiens
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Analyse_Delegues_${dayjs().format('DD_MM_YYYY')}.csv`;
    link.click();
};

  const renderCalendarTable = (record) => {
    const days = [];
    let current = dayjs(startDate).startOf('day');
    const endLimit = dayjs(endDate).endOf('day');

    while (current.isBefore(endLimit)) {
      days.push(getDayData(record, current));
      current = current.add(1, 'day');
    }

    return (
      <Table 
        dataSource={days} 
        pagination={false} 
        size="small" 
        bordered 
        rowKey="dateStr"
        columns={[
          { 
            title: 'Date', 
            dataIndex: 'dateStr', 
            key: 'date', 
            width: 120, 
            render: (t, r) => <Text strong style={{ fontSize: '12px' }}>{t} <br/><small style={{color:'#8c8c8c'}}>({r.dayName})</small></Text> 
          },
          
          { 
            title: 'Régions (Nb)', 
            dataIndex: 'regionsText', 
            key: 'regions',
            width: 160, 
            render: (text) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {text !== '-' ? text.split(' - ').map((r, i) => (
                  <Tag key={i} color="blue" style={{ margin: 0, fontSize: '10px', width: 'fit-content' }}>
                    {r}
                  </Tag>
                )) : '-'}
              </div>
            )
          },

          // --- COLONNE ADRESSES (PLUS LARGE ET PLUS GRANDE) ---
          { 
            title: 'Adresses visitées', 
            dataIndex: 'addressesText', 
            key: 'addresses',
            width: 450, // Largeur augmentée
            render: (text) => (
              <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px', color: '#262626', lineHeight: '1.5' }}>
                {text !== '-' ? text.split(' | ').map((addr, i) => (
                  <div key={i} style={{ borderBottom: '1px solid #f0f0f0', padding: '4px 0', whiteSpace: 'normal' }}>
                    <span style={{ color: '#1890ff', marginRight: '5px' }}>•</span> {addr}
                  </div>
                )) : <Text type="secondary">-</Text>}
              </div>
            )
          },

          { 
            title: 'Visites', 
            dataIndex: 'totalVisits', 
            key: 'total', 
            align: 'center', 
            width: 70, 
            render: (v) => v > 0 ? <Badge count={v} style={{ backgroundColor: '#52c41a' }} /> : <Text type="secondary">0</Text> 
          },
          { 
            title: 'Congés', 
            key: 'leave', 
            align: 'center', 
            width: 80, 
            render: (_, r) => r.isLeave ? <Tag color="error" style={{ fontSize: '9px', margin: 0 }}>CONGÉ</Tag> : '-' 
          },
          { 
            title: 'Fériés', 
            dataIndex: 'holiday', 
            key: 'holiday', 
            width: 100, 
            render: (t) => t !== '-' ? <Tag color="warning" style={{ fontSize: '9px', margin: 0 }}>{t}</Tag> : '-' 
          },

          // --- COLONNE ÉVÉNEMENT (DÉPLACÉE À LA FIN) ---
          { 
            title: 'Événement', 
            dataIndex: 'eventName', 
            key: 'event',
            width: 160,
            render: (text) => text !== '-' ? (
              <Tag color="orange" icon={<RocketOutlined />} style={{ fontSize: '10px', fontWeight: 'bold', whiteSpace: 'normal', width: '100%' }}>
                {text.toUpperCase()}
              </Tag>
            ) : '-'
          },

          { 
            title: 'Km Optimal (Estimé)', 
            dataIndex: 'optimalKm', 
            key: 'optimalKm', 
            align: 'center',
            width: 130, 
            render: (km, record) => {
              if (km === null || record.totalVisits === 0) return <Text type="secondary">-</Text>;
              return (
                <Tooltip title={`Ordre suggéré : ${record.optimalPathText}`}>
                  <Tag color="cyan" style={{ fontWeight: 'bold', fontSize: '11px', cursor: 'pointer' }}>
                    📍 {km} km
                  </Tag>
                </Tooltip>
              );
            }
          },
          { 
            title: 'Km Réel (Visites)', 
            dataIndex: 'realKm', 
            key: 'realKm', 
            align: 'center',
            width: 130, 
            render: (km, record) => {
              if (km === null || record.totalVisits === 0) return <Text type="secondary">-</Text>;
              
              // Comparaison : Si le réel dépasse l'optimal de plus de 30%, on alerte en orange/rouge
              const diff = km - (record.optimalKm || 0);
              const isDetour = record.optimalKm > 0 && diff > 8;

              return (
                <Tag color={isDetour ? "volcano" : "purple"} style={{ fontWeight: 'bold', fontSize: '11px' }}>
                  🚗 {km} km
                </Tag>
              );
            }
          },
        ]}
        rowClassName={(r) => {
            const day = dayjs(r.dateStr, 'DD/MM/YYYY').day();
            if (r.isLeave) return 'row-leave';
            if (r.holiday !== '-') return 'row-holiday';
            if (day === 0 || day === 6) return 'row-weekend';
            return '';
        }}
      />
    );
  };

  return (
    <div className="tabled">
      <style>{`
        .row-weekend { background-color: #f5f5f5 !important; }
        .row-leave { background-color: #fff1f0 !important; color: #f5222d; }
        .row-holiday { background-color: #feffe6 !important; }
        .ant-table-expanded-row { background: #fafafa !important; }
      `}</style>
      
      <Title level={4}>Résumé Analytique des Délégués</Title>
      
      <Card size="small" style={{ marginBottom: 20, background: '#fbfbfb' }}>
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ dateRange: [dayjs('2026-03-01'), dayjs('2026-03-31')] }}>
          <Row gutter={16}>
            <Col span={8}>
                <Form.Item name="delegateIds" label="Délégués">
                    <Select 
                        mode="multiple" 
                        allowClear 
                        showSearch // <-- PERMET D'ÉCRIRE DANS LA BOITE
                        placeholder="Chercher un délégué"
                        optionFilterProp="label" // <-- FILTRE SUR LE TEXTE ET NON L'ID
                        options={delegatesListResponse?.data?.map(d => ({ 
                            label: `${d.firstname} ${d.lastname}`.toUpperCase(), 
                            value: d.id 
                        }))} 
                    />
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item name="regionIds" label="Régions">
                    <Select 
                        mode="multiple" 
                        allowClear 
                        showSearch // <-- PERMET D'ÉCRIRE DANS LA BOITE
                        placeholder="Chercher une région"
                        optionFilterProp="label" // <-- FILTRE SUR LE TEXTE
                        options={regionsListResponse?.data?.map(r => ({ label: r.name, value: r.id }))} 
                    />
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item name="dateRange" label="Période">
                    <RangePicker style={{ width: '100%' }} />
                </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: 'right' }}>
              <Button onClick={() => { form.resetFields(); dispatch(getDelegatesSummary({})); }} style={{ marginRight: 8 }}>Réinitialiser</Button>
              <Button type="primary" htmlType="submit" loading={summaryLoading} style={{ marginRight: 8 }}>Filtrer</Button>
              <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>Exporter CSV</Button>
          </div>
        </Form>
      </Card>

      <Table 
        columns={[
          { title: 'Délégué', key: 'name', render: (_, r) => <Space><UserOutlined /><Text strong>{r.firstname} {r.lastname}</Text></Space> },
          { title: 'Total Visites Période', dataIndex: 'reportCount', key: 'total', align: 'center', render: (v) => <Tag color="blue" style={{fontSize:'14px'}}>{v}</Tag> },
          { title: 'Actions', align: 'center', render: () => <Text type="secondary"><InfoCircleOutlined /> Cliquez sur + pour le détail</Text> }
        ]} 
        dataSource={summaryData} 
        rowKey="id" 
        loading={summaryLoading} 
        bordered 
        expandable={{ expandedRowRender: record => renderCalendarTable(record) }} 
      />
    </div>
  );
};

export default DelegatesSummaryPage;